import { Button, Form, Modal, Image, Upload, message, Card } from 'antd';
import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import style from './ImageGallery.module.css';
import './ImageGallery.module.css';

import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';

import type { UploadChangeParam } from 'antd/es/upload';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import { ImagesHelper } from '../../Helpers/ImagesHelper';
import { HttpHelper } from '../../Helpers/HttpHelper';
import { DbImageData } from '../../Models/ImageData/DbImageData';

import styled from 'styled-components';
import { serviceBundler } from '../../App';

const CustomCard = styled(Card)`
  .ant-card-body {
    padding: 10px;
    border-radius: 0;
  }
`;

export function ImageGallery(props: {
  open: boolean;
  onCancel: Function;
  onChooseFuncEnabled: boolean;
  onChoose?: Function;
}) {
  const [form] = Form.useForm();

  const { i18n, t } = useTranslation();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [imagesData, setImagesData] = useState<DbImageData[]>();

  const [imageUrl, setImageUrl] = useState<string>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [keyForceUpdate, setKeyForceUpdate] = useState(0);

  useEffect(() => {
    CreateFileList();
  }, [0]);

  const handleCancel = () => {
    props.onCancel();
  };

  const GetRemoteLocation = () => {
    return HttpHelper.GetUrlStorageBaseApi();
  };

  const CreateFileList = async () => {
    var files = await ImagesHelper.GetFiles();
    setImagesData(files);

    const newFileList: UploadFile[] = [];

    files.forEach((file: DbImageData) => {
      newFileList.push({
        uid: file.id,
        name: file.fileName,
        status: 'done',
        url: GetRemoteLocation() + file.urlLocationOnServer,
      });

      setFileList(newFileList);
    });
  };

  const GetUploadUrl = (): string => {
    return ImagesHelper.UploadImage();
  };

  const handleRemove = async (file: UploadFile) => {
    var result = await ImagesHelper.RemoveImage(file.uid);
    if (result === '') {
      var index = fileList.findIndex((item) => item.uid === file.uid);
      fileList.splice(index, 1);
      setFileList([...fileList]);
      message.success('File removed successfully!');
    }
  };

  const handleChange: UploadProps['onChange'] = (
    info: UploadChangeParam<UploadFile>,
  ) => {
    //merge the new file list with the existing one
    setFileList((prev) => {
      const newFileList = [...prev];
      const index = newFileList.findIndex((item) => item.uid === info.file.uid);
      if (index >= 0) {
        newFileList[index] = info.file;
      } else {
        if (info.file.percent === 100) {
          newFileList.push(info.file);
        }
      }

      setKeyForceUpdate(newFileList.length);

      return newFileList;
    });

    CreateFileList();

    if (info.file.status === 'uploading') {
      setLoading(false);
      return;
    }
    if (info.file.status === 'done') {
      getBase64(info.file.originFileObj as RcFile, (url) => {
        setLoading(false);
        setImageUrl(url);
      });
    }
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      getBase64(file.originFileObj as RcFile, (url) => {
        setLoading(false);
        setImageUrl(url);
        file.preview = url;
      });
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1),
    );
  };

  const getBase64 = (img: RcFile, callback: (url: string) => void) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result as string));
    reader.readAsDataURL(img);
  };

  const beforeUpload = (file: RcFile) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!');
    }
    const isLt2M = file.size / 1024 / 1024 < 10;
    if (!isLt2M) {
      message.error('Image must smaller then 10MB!');
    }
    return isJpgOrPng && isLt2M;
  };

  const removeImage = (file: UploadFile) => () => {
    ImagesHelper.RemoveImage(file.uid);
    var index = fileList.findIndex((item) => item.uid === file.uid);
    fileList.splice(index, 1);
    setFileList([...fileList]);
    setKeyForceUpdate(fileList.length);
    message.success(t('fileDeleted'));
  };

  const setAsImage = (file: UploadFile) => () => {
    var foundImageData = imagesData?.find((item) => item.id === file.uid);
    if (foundImageData) {
      if (props.onChoose) {
        props.onChoose(foundImageData);
      }
      props.onCancel();
    } else {
      message.error(t('imageNotFound'));
    }
  };

  return (
    <Modal
      title={t('manageImages')}
      open={props.open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      footer={[
        <Button key="back" onClick={handleCancel}>
          {t('cancel')}
        </Button>,
      ]}
      style={{ minWidth: '600px' }}
    >
      <Image.PreviewGroup
        // preview={{
        //   onChange: (current, prev) =>
        //     console.log(`current index: ${current}, prev index: ${prev}`),
        // }}
        key={keyForceUpdate}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            marginBottom: '10px',
            flexDirection: 'row',
          }}
        >
          {fileList.map((file) => {
            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <Button onClick={removeImage(file)} type="primary">
                  {t('remove')}
                </Button>
                {props.onChooseFuncEnabled && (
                  <Button onClick={setAsImage(file)} type="primary">
                    {t('setAsImage')}
                  </Button>
                )}

                <CustomCard className={style.imageCard} variant='outlined'>
                  <Image
                    key={file.uid}
                    width={80}
                    src={file.url}
                    onClick={() => {
                      setPreviewOpen(true);
                      setPreviewImage(file.url!);
                      setPreviewTitle(file.name!);
                    }}
                  />
                </CustomCard>
              </div>
            );
          })}
        </div>
      </Image.PreviewGroup>
      <Upload
        listType="picture-card"
        // fileList={fileList}
        showUploadList={false}
        action={GetUploadUrl()}
        onPreview={handlePreview}
        onChange={handleChange}
        onRemove={handleRemove}
        beforeUpload={beforeUpload}
        accept="image/*"
      >
        <div>
          {loading ? <LoadingOutlined /> : <PlusOutlined />}
          <div style={{ marginTop: 8 }}>Upload</div>
        </div>
      </Upload>
    </Modal>
  );
}
